import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import api from "../services/api";

type LatestVersionResponse = {
  android: { versionCode: number | null; versionName: string | null; storeUrl: string };
  ios: { build: number | null; version: string | null; storeUrl: string };
};

type UpdateInfo = { current: string; latest: string; storeUrl: string } | null;

/**
 * Compares the running native build number against the "latest
 * published" number returned by GET /version/latest. When the installed
 * copy is behind, renders a sticky bottom banner with an "Update"
 * button that opens the Play Store / App Store listing.
 *
 * Runs only inside the Capacitor native container — web sessions have
 * nothing to update to. Compares build numbers (versionCode /
 * CFBundleVersion), not marketing version strings, because those are
 * monotonic integers and safe to compare with `<`.
 *
 * Silently no-ops on any error (backend down, plugin unavailable,
 * user offline) — an update banner is convenience, never critical
 * path.
 */
export default function UpdateAvailableBanner() {
  const [info, setInfo] = useState<UpdateInfo>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;

    (async () => {
      try {
        const platform = Capacitor.getPlatform(); // 'ios' | 'android'
        const [{ data: latest }, appInfo] = await Promise.all([
          api.get<LatestVersionResponse>("/version/latest"),
          CapApp.getInfo(),
        ]);
        if (cancelled) return;

        const currentBuild = parseInt(String(appInfo.build), 10);
        if (!Number.isFinite(currentBuild)) return;

        if (platform === "android" && latest.android.versionCode != null) {
          if (currentBuild < latest.android.versionCode) {
            setInfo({
              current: appInfo.version,
              latest: latest.android.versionName || String(latest.android.versionCode),
              storeUrl: latest.android.storeUrl,
            });
          }
        } else if (platform === "ios" && latest.ios.build != null) {
          if (currentBuild < latest.ios.build) {
            setInfo({
              current: appInfo.version,
              latest: latest.ios.version || String(latest.ios.build),
              storeUrl: latest.ios.storeUrl,
            });
          }
        }
      } catch {
        // Silent no-op — version check is convenience, not critical.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!info || dismissed) return null;

  const openStore = () => {
    // window.open on native falls back to opening in the system
    // browser, which iOS+Android will route straight to the store
    // listing when the URL is a play.google.com / apps.apple.com link.
    window.open(info.storeUrl, "_blank");
  };

  return (
    <div className="fixed left-3 right-3 bottom-[calc(6rem+var(--android-nav-inset,0px))] z-[60] bg-indigo-600 text-white rounded-xl shadow-lg border border-indigo-500 p-3 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Versiune nouă disponibilă</p>
        <p className="text-xs text-white/80 truncate">
          Instalată: {info.current} · Nouă: {info.latest}
        </p>
      </div>
      <button
        onClick={openStore}
        className="flex-shrink-0 px-3 py-1.5 bg-white text-indigo-700 rounded-lg text-sm font-semibold hover:bg-indigo-50 transition-colors"
      >
        Actualizează
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Închide"
        className="flex-shrink-0 p-1 text-white/70 hover:text-white transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
